import React from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Calendar, CreditCard, ChevronRight, AlertTriangle } from 'lucide-react';
import { useStudentFinanceWidget } from '@/shared/hooks/useDashboard';

interface FinanceWidgetProps {
  linkTo?: string;
}

export const FinanceWidget: React.FC<FinanceWidgetProps> = ({
  linkTo = '/compte-financier',
}) => {
  const { data: finance, isLoading, error } = useStudentFinanceWidget();

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="space-y-3">
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !finance) {
    return null; // Don't show widget if no finance data
  }

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-MA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const hasOutstandingBalance = finance.balance > 0;
  const isOverdue = finance.nextInstallment && new Date(finance.nextInstallment.dueDate) < new Date();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Situation Financière</h2>
        <Link
          to={linkTo}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          Voir détails
          <ChevronRight size={16} />
        </Link>
      </div>

      <div className="p-6 space-y-4">
        {/* Balance */}
        <div className={`flex items-center justify-between p-4 rounded-lg ${
          hasOutstandingBalance ? 'bg-red-50' : 'bg-green-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              hasOutstandingBalance ? 'bg-red-100' : 'bg-green-100'
            }`}>
              <Wallet size={20} className={
                hasOutstandingBalance ? 'text-red-600' : 'text-green-600'
              } />
            </div>
            <div>
              <p className="text-sm text-gray-600">Solde à Payer</p>
              <p className={`text-lg font-bold ${
                hasOutstandingBalance ? 'text-red-700' : 'text-green-700'
              }`}>
                {formatCurrency(finance.balance)} DH
              </p>
            </div>
          </div>
          {hasOutstandingBalance && (
            <AlertTriangle size={20} className="text-red-500" />
          )}
        </div>

        {/* Next Installment */}
        {finance.nextInstallment && (
          <div className={`flex items-center justify-between p-4 rounded-lg ${
            isOverdue ? 'bg-amber-50' : 'bg-blue-50'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                isOverdue ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                <Calendar size={20} className={
                  isOverdue ? 'text-amber-600' : 'text-blue-600'
                } />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {isOverdue ? 'Échéance dépassée' : 'Prochaine Échéance'}
                </p>
                <p className={`text-lg font-bold ${
                  isOverdue ? 'text-amber-700' : 'text-blue-700'
                }`}>
                  {formatCurrency(finance.nextInstallment.amount)} DH
                </p>
                <p className="text-xs text-gray-500">
                  {finance.nextInstallment.label} - {formatDate(finance.nextInstallment.dueDate)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Last Payment */}
        {finance.lastPayment && (
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100">
                <CreditCard size={20} className="text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Dernier Paiement</p>
                <p className="text-lg font-bold text-gray-700">
                  {formatCurrency(finance.lastPayment.amount)} DH
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(finance.lastPayment.date)} - {finance.lastPayment.method}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceWidget;
